'use client';

import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';
import type { MediaPickerLibraryItem, UploadResult } from '@kit/uploads';

import {
  AUDITION_TYPES,
  MAX_TITLE_LENGTH,
} from '../../constants/audition.constants';
import { recordTypeOf } from '../../lib/record-terminology';
import type { UpdateAuditionInput } from '../../schemas/audition.schema';
import type { AuditionWithDates } from '../../types/audition.types';
import { CompetitionPublicTab } from './competition-public-tab';

interface BasicInfoTabProps {
  form: UseFormReturn<UpdateAuditionInput>;
  audition: AuditionWithDates;
  /** Org media library + id for the competition cover picker (see AuditionForm). */
  orgMedia?: { organizationId: string; items: MediaPickerLibraryItem[] } | null;
  coverUploadAction?: (formData: FormData) => Promise<UploadResult>;
}

export function BasicInfoTab({
  form,
  audition,
  orgMedia,
  coverUploadAction,
}: BasicInfoTabProps) {
  const { t } = useTranslation();
  const isCompetition = recordTypeOf(audition) === 'competition';
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.basic.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.basic.description" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title */}
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.basic.titleLabel" />
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('auditions:formTabs.basic.titlePlaceholder')}
                    maxLength={MAX_TITLE_LENGTH}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  <Trans
                    i18nKey="auditions:formTabs.basic.charCount"
                    values={{
                      count: field.value?.length || 0,
                      max: MAX_TITLE_LENGTH,
                    }}
                  />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.basic.descriptionLabel" />
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t(
                      'auditions:formTabs.basic.descriptionPlaceholder',
                    )}
                    className="min-h-[150px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Audition Type */}
          <FormField
            control={form.control}
            name="auditionType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.basic.typeLabel" />
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value || ''}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          'auditions:formTabs.basic.typePlaceholder',
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {AUDITION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-muted-foreground text-xs">
                            {type.description}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* External Link */}
          <FormField
            control={form.control}
            name="externalLink"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.basic.externalLinkLabel" />
                </FormLabel>
                <FormControl>
                  <Input type="url" placeholder="https://..." {...field} />
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="auditions:formTabs.basic.externalLinkHint" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Additional Information */}
          <FormField
            control={form.control}
            name="additionalInformations"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.basic.additionalLabel" />
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t(
                      'auditions:formTabs.basic.additionalPlaceholder',
                    )}
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      {/* Competition-only branding / public-page / livestream sections, folded
          in from the former "Public page & branding" tab. */}
      {isCompetition && (
        <CompetitionPublicTab
          form={form}
          orgMedia={orgMedia}
          coverUploadAction={coverUploadAction}
        />
      )}
    </div>
  );
}
